import { useState } from 'react';
import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as SettingIcon } from '@Assets/setting_icon.svg';
import {
  PageWrapper,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@Components';
import { ConfigOSA } from '@Models';
import AuditAlertSection from './sections/AuditAlertSection';
import OsaCalculationSection from './sections/OsaCalculationSection';

interface ConfigurationPolicyOSAProps {
  config: ConfigOSA;
  readOnly?: boolean;
}

const ConfigurationPolicyOSA = ({ config, readOnly = true }: ConfigurationPolicyOSAProps) => {
  const [activeTab, setActiveTab] = useState('osaCalculation');
  const { t } = useTranslation();

  const tabs = [
    { id: 'osaCalculation', label: t('ConfigurationPolicy.OSA.OsaCalculation') },
    { id: 'auditAlerts', label: t('ConfigurationPolicy.OSA.AuditAlerts') },
  ];

  return (
    <div className="h-full w-full">
      <PageWrapper
        title={t('ConfigurationPolicy.OSA.Title')}
        description={
          <span className="flex items-center gap-2 text-[17px] text-gray-600">
            <SettingIcon stroke="#667085" fill="none" strokeWidth={1.5} className="w-[19px] h-[19px]" />
            {config.Connector}
          </span>
        }
        actions={
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-gray-600 cursor-help">
                  <Lock size={18} />
                </span>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>{t('ConfigurationPolicy.LockIconTooltip')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        }>
        <div className="flex flex-col gap-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full grid grid-cols-2 h-auto">
              {tabs.map((tab, index) => {
                const isActive = tab.id === activeTab;
                const isFirst = index === 0;
                const isLast = index === tabs.length - 1;
                const borderClass = isActive ? (isFirst ? 'border-r' : isLast ? 'border-l' : 'border-l border-r') : '';

                return (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.id}
                    className={`py-3 bg-gray-50 border-0 text-gray-500 text-[15px] border-gray-200 rounded-md
                    data-[state=active]:bg-white
                    ${borderClass}
                    data-[state=active]:border-solid
                    data-[state=active]:h-auto
                    data-[state=active]:border-gray-200
                    data-[state=active]:shadow-sm
                    data-[state=active]:text-gray-700 cursor-pointer`}>
                    {tab.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            <div className="mt-4">
              <TabsContent value="osaCalculation" className="animate-slide-in">
                <OsaCalculationSection data={config.Config.OsaCalculation} readOnly={readOnly} />
              </TabsContent>

              <TabsContent value="auditAlerts" className="animate-slide-in">
                <AuditAlertSection data={config.Config.AuditAlerts} readOnly={readOnly} />
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </PageWrapper>
    </div>
  );
};

export default ConfigurationPolicyOSA;
